import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { collect } from 'collect.js';
import { Drawer, Progress } from 'antd';
import classnames from 'classnames';
import OnboardingGuideStep from './OnboardingGuideStep';
import { ONBOARDING_GUIDE_STEPS } from '../../services/exports/Constants';
import useVerification from '../../hooks/verification/useVerification';
import useApiClient from '../../hooks/api/useApiClient';
import usePermissions, { ManagerRole } from '../../hooks/auth/usePermissions';
import useScreenType from '../../hooks/context/useScreenType';
import useTheme from '../../hooks/context/useTheme';
import usePlatformAgentState from '../../hooks/context/usePlatformAgentState';

export default function OnboardingGuide() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Onboarding:OnboardingGuide' });

  const { company } = useSelector((state) => state.currentCompany);
  const merchant = company?.merchant ?? {};

  const theme = useTheme();

  const { OnboardingStepsManager } = useApiClient();
  const { isRole } = usePermissions();
  const { isComplete: onboardingComplete } = useVerification();

  const { isDesktop } = useScreenType();
  // The Ask Amigo row owns the bottom right corner; the collapsed checklist sits above it (20 + 48 + 12 px).
  const { available: platformAgentShown } = usePlatformAgentState();
  // …and while the panel is open it would lie on the panel, so it waits until the panel closes.
  const platformAgentOpen = useSelector((state) => state.platformAgent.open) && platformAgentShown;
  // A bar fixed to the page's bottom (useBottomBar: the offer pages' save bar) lifts the Ask Amigo row to 12 px above
  // it; the checklist goes up with it, so it never lies on Ask Amigo nor on the bar (QA91).
  const bottomBar = useSelector((state) => state.platformAgent.bottomBar);
  const cornerBottom = bottomBar > 0 ? bottomBar + 12 : platformAgentShown ? 20 : 16;
  const checklistBottom = platformAgentShown ? cornerBottom + 48 + 12 : cornerBottom;

  const [groups, setGroups] = useState([]);
  const [collapsed, setCollapsed] = useState(true);

  const data = useMemo(
    () =>
      collect(groups)
        .where('is_complete', false)
        .push({
          id: ONBOARDING_GUIDE_STEPS.Complete,
          is_complete: true,
          children: collect(groups).where('is_complete', true).pluck('children').flatten(1).toArray(),
        })
        .toArray(),
    [groups],
  );

  const stepsComplete = collect(groups).pluck('children').flatten(1).where('is_complete', true).count();
  const stepsTotal = collect(groups).pluck('children').flatten(1).count();
  const isComplete = !!merchant?.onboarding_guide_complete_at || stepsComplete === stepsTotal;

  useEffect(() => {
    loadData();
  }, [company?.id]);

  const toggle = () => setCollapsed((current) => !current);

  const loadData = async () => {
    if (!company?.id || isRole(ManagerRole.ContentManager)) {
      return;
    }

    const { success, data } = await OnboardingStepsManager.get();

    if (!success) {
      return;
      // return toast.error(t('toasts.failed_to_load_onboarding'));
    }

    setGroups(data);
  };

  const renderProgress = () => (
    <Progress
      type="circle"
      percent={(stepsComplete / stepsTotal) * 100}
      format={() => `${stepsComplete}/${stepsTotal}`}
      strokeColor={theme.color.brand.primary}
      size="small"
    />
  );

  if (isComplete || !onboardingComplete) {
    return null;
  }

  if (collapsed) {
    if (platformAgentOpen) {
      return null;
    }

    return (
      <div
        onClick={toggle}
        className={classnames(
          'fixed right-0 z-50 bottom-0 h-20 w-20 cursor-pointer hover:animate-scale animate-pulse',
          platformAgentShown ? 'mr-5' : 'mr-4',
        )}
        style={{ marginBottom: checklistBottom }}
      >
        {renderProgress()}
      </div>
    );
  }

  return (
    <Drawer open onClose={toggle} size={window.innerWidth * (isDesktop ? 0.5 : 1)}>
      <>
        <div className="flex mt-2">
          <div className="h-20 w-20">{renderProgress()}</div>
          <div className="ml-4">
            <h6>{t('title')}</h6>
            <p>{t('description')}</p>
          </div>
        </div>
        <div className="pb-6">
          {data.map(
            (item) =>
              item.children.length > 0 && (
                <React.Fragment key={`onboarding-step-${item.id}`}>
                  <h6 className="mt-6">{t(`steps.${item.id}`)}</h6>
                  {item.children.map((child) => (
                    <OnboardingGuideStep
                      step={child.id}
                      isComplete={child.is_complete}
                      onComplete={loadData}
                      className="mt-2"
                      key={`onboarding-step-${child.id}`}
                    />
                  ))}
                </React.Fragment>
              ),
          )}
        </div>
      </>
    </Drawer>
  );
}
